'use client'
import { useContext, useEffect, useMemo } from 'react'
import type { NextPage } from 'next'
import Image from 'next/image'
import { useRouter, useSearchParams } from 'next/navigation'
import Head from 'next/head'
import { Lock } from 'lucide-react'

import { isRegistrationComplete } from '../../services/auth'
import logo from '../../public/logo.svg'
import AuthContext from '../../context/AuthContext'
import ThirdPartySignInButton from '../../components/Login/ThirdPartySignInButton'
import FeatureShowcase from './FeatureShowcase'
import CampaignPackageBuyJune25Showcase, {
	PackageBuyJune25CampaignDataSchema,
} from './CampaignPackageBuyJune25Showcase'

const Login: NextPage = () => {
	const authContext = useContext(AuthContext)
	const router = useRouter()
	const searchParams = useSearchParams()
	const inviteCode = searchParams?.get('invite')
	const cid = searchParams?.get('cid')
	const token = searchParams?.get('token')

	// Parse and validate the email campaign token
	const campaignPackageBuyJune25Data = useMemo(() => {
		if (!cid || !token) return null

		try {
			const decodedToken = Buffer.from(token, 'base64').toString('utf-8')
			const parsedData = JSON.parse(decodedToken)

			const validationResult = PackageBuyJune25CampaignDataSchema.safeParse(parsedData)
			if (validationResult.success) {
				return validationResult.data
			}
		} catch (error) {
			console.warn('Failed to parse email campaign token:', error)
		}

		return null
	}, [cid, token])

	useEffect(() => {
		if (authContext.loggedIn) {
			const query: { invite?: string } = {}
			if (inviteCode) {
				query.invite = inviteCode
			}
			if (!isRegistrationComplete(authContext.user)) {
				router.replace(`/register?${searchParams.toString()}`)
			} else {
				router.replace(`/dashboard/members?${searchParams.toString()}`)
			}
		}
	}, [authContext.loggedIn, authContext.user, authContext.loading, inviteCode, router, searchParams])

	if (authContext.loading) {
		return null
	}

	return (
		<div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
			<Head>
				<title>Hit Em Up | Login</title>
			</Head>
			<div className="flex flex-col p-8 md:p-12 lg:p-16">
				<div className="mb-12">
					<div className="flex items-center gap-2">
						<Image src={logo} alt="Hit Em Up" width="230" />
					</div>
				</div>
				<div className="flex flex-1 flex-col justify-center space-y-6">
					<div className="space-y-2">
						<h1 className="text-3xl font-semibold tracking-tight">
							{campaignPackageBuyJune25Data ? "Don't Lose Your Subscription" : 'Admin Login'}
						</h1>
						<p className="text-gray-500">
							{inviteCode
								? 'Sign in to your family account.'
								: 'Manage users, billing, and upload contacts for your family account.'}
						</p>
					</div>
					<div className="space-y-4">
						{campaignPackageBuyJune25Data?.email && (
							<p className="text-lg text-red-600 font-medium text-center">
								Sign in with {campaignPackageBuyJune25Data.email}
							</p>
						)}
						{campaignPackageBuyJune25Data?.providerId === 'google.com' ? (
							<ThirdPartySignInButton provider="google" />
						) : campaignPackageBuyJune25Data?.providerId === 'apple.com' ? (
							<ThirdPartySignInButton provider="apple" />
						) : (
							<>
								<ThirdPartySignInButton provider="google" />
								<ThirdPartySignInButton provider="apple" />
							</>
						)}
					</div>
					<div className="flex items-center justify-center gap-2 mt-4 text-gray-500">
						<Lock size={16} />
						<span className="text-sm">Secure login • Your data is protected</span>
					</div>
					<div className="text-center mt-6">
						<p className="text-gray-600">
							Need help? Call us at{' '}
							<a href="tel:+14156401667" className="text-hitemup-green font-medium hover:underline">
								+1 (415) 640-1667
							</a>
						</p>
					</div>
					<p className="text-sm text-gray-500 text-center mt-6">
						<span className="font-bold">Note:</span> This dashboard is for account and contact management
						only. To send messages, please use the Hit Em Up mobile app.
					</p>
				</div>

				{/* Mobile Campaign Showcase - moved below login form */}
				{campaignPackageBuyJune25Data && (
					<div className="md:hidden mt-8 bg-hitemup-green p-8 text-white mx-[-32px] mb-[-32px]">
						<CampaignPackageBuyJune25Showcase data={campaignPackageBuyJune25Data} />
					</div>
				)}
			</div>
			<div className="hidden bg-hitemup-green md:flex md:flex-col md:items-center md:justify-center p-12 text-white">
				{campaignPackageBuyJune25Data ? (
					<CampaignPackageBuyJune25Showcase data={campaignPackageBuyJune25Data} />
				) : (
					<FeatureShowcase />
				)}
			</div>
		</div>
	)
}

export default Login
